import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { M8tCommand } from "../../lib/m8t-command.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import { runAz } from "../../lib/az.js";
import { getAzAccount } from "../../lib/auth.js";
import { parseContainerAppResourceId } from "../../lib/platform-update.js";
import { fetchManifest, type ManifestSource } from "../../lib/release-channel.js";
import { fetchRepoTree, type RepoTree } from "../../lib/release-content.js";
import { readStamp } from "../../lib/platform-stamp.js";
import { statusRows, type StatusRow } from "../../lib/platform-converge.js";
import { colors, renderJson, renderTable, resolveOutputMode } from "../../lib/output.js";

interface LiveVerify {
  liveGatewayTag: string;
  stampGatewayTag: string;
  gatewayTagMatch: boolean | "unknown";
  apiVersion: string;
}

export class PlatformStatusCommand extends M8tCommand {
  static paths = [["platform", "status"]];
  static usage = Command.Usage({
    description: "Show per-component platform drift against a release manifest.",
    details:
      "Fetches a release manifest (the current channel by default), reads the installed-state stamp, and reports each component's installed vs target version and drift status. Read-only — never writes the stamp, never converges anything (use 'm8t platform update' for that). --verify additionally probes the live gateway (image tag + /api/version) best-effort.",
  });

  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group", {
    description: "m8t resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });
  to = Option.String("--to", {
    description: "Check status against a pinned platform version (defaults to the current release channel).",
  });
  contentDir = Option.String("--content-dir", {
    description: "Path to a local m8t repo checkout (skips the tarball tree-SHA fetch).",
  });
  manifestUrl = Option.String("--manifest-url", {
    description: "Fetch the release manifest from this URL instead of the release channel.",
  });
  manifestFile = Option.String("--manifest-file", {
    description: "Read the release manifest from this local file instead of the release channel.",
  });
  verify = Option.Boolean("--verify", false, {
    description: "Additionally probe the live gateway (image tag + /api/version) and report stamp-vs-live.",
  });
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const log = (msg: string) => {
      if (mode !== "json") this.context.stdout.write(msg + "\n");
    };

    const to = typeof this.to === "string" ? this.to : undefined;
    const contentDirFlag = typeof this.contentDir === "string" ? this.contentDir : undefined;
    const manifestUrl = typeof this.manifestUrl === "string" ? this.manifestUrl : undefined;
    const manifestFile = typeof this.manifestFile === "string" ? this.manifestFile : undefined;

    const gw = await resolveGatewayContext({
      interactive: mode !== "json",
      subscriptionId: this.subscription,
      resourceGroup: this.resourceGroup,
    });
    const { resourceGroup } = parseContainerAppResourceId(gw.containerAppResourceId);
    const credential = new DefaultAzureCredential();
    const account = await getAzAccount();
    const subscriptionId = this.subscription ?? account.subscriptionId;

    const source: ManifestSource = manifestFile ? { file: manifestFile } : manifestUrl ? { url: manifestUrl } : { channel: true, version: to };
    const manifest = await fetchManifest(source);

    // status is strictly read-only: readStamp only, NEVER writeStamp.
    const stamp = await readStamp({ credential, subscriptionId, resourceGroup });

    const tree: RepoTree = contentDirFlag ? { treeShaOf: () => undefined } : await fetchRepoTree(manifest.platform.commit);
    const rows = statusRows(manifest, stamp, tree);

    let verify: LiveVerify | undefined;
    if (this.verify) {
      verify = await this.probeLive(gw.containerAppResourceId, gw.gatewayUrl, stamp?.components.gateway.tag);
    }

    if (mode === "json") {
      this.context.stdout.write(
        renderJson({
          kind: "platform-status",
          gateway: resourceGroup,
          targetVersion: manifest.platform.tag,
          managed: stamp !== null,
          rows,
          ...(verify ? { verify } : {}),
        }) + "\n",
      );
      return 0;
    }

    log(colors.field(`target: ${manifest.platform.tag}${stamp === null ? "  (unmanaged — no stamp found)" : ""}`));
    this.context.stdout.write(this.renderRows(rows) + "\n");

    if (verify) {
      log("");
      log(colors.field("live verify:"));
      log(`  stamp gateway tag:  ${verify.stampGatewayTag}`);
      log(`  live gateway tag:   ${verify.liveGatewayTag}`);
      log(
        `  match:               ${verify.gatewayTagMatch === "unknown" ? colors.warn("unknown") : verify.gatewayTagMatch ? colors.success("yes") : colors.warn("no")}`,
      );
      log(`  /api/version:        ${verify.apiVersion}`);
    }

    return 0;
  }

  private renderRows(rows: StatusRow[]): string {
    const tableRows = rows.map((r) => ({
      component: r.personaName ? `personas:${r.personaName}` : r.component,
      installed: r.installed,
      target: r.target,
      status: r.status,
    }));
    return renderTable(tableRows, [
      { key: "component", label: "COMPONENT" },
      { key: "installed", label: "INSTALLED" },
      { key: "target", label: "TARGET" },
      { key: "status", label: "STATUS" },
    ]);
  }

  /**
   * Best-effort live probe for --verify: the live gateway image tag (az) and
   * GET /api/version. Any failure (auth-gated endpoint, transient network
   * error, etc.) degrades the affected field to "unknown" rather than
   * throwing — a verify probe must never crash the read-only status command.
   */
  private async probeLive(gatewayResourceId: string, gatewayUrl: string, stampGatewayTag: string | undefined): Promise<LiveVerify> {
    let liveGatewayTag = "unknown";
    try {
      const { resourceGroup, name } = parseContainerAppResourceId(gatewayResourceId);
      const image = (
        await runAz([
          "containerapp", "show", "-g", resourceGroup, "-n", name,
          "--query", "properties.template.containers[0].image", "-o", "tsv",
        ])
      ).trim();
      const tag = image.split(":").pop();
      if (tag) liveGatewayTag = tag;
    } catch {
      liveGatewayTag = "unknown";
    }

    let apiVersion = "unknown";
    try {
      const res = await fetch(`${gatewayUrl}/api/version`, { method: "GET" });
      if (res.ok) {
        const body = (await res.json().catch(() => ({}))) as { version?: string };
        apiVersion = body.version ?? "unknown";
      } else if (res.status === 401 || res.status === 403) {
        apiVersion = "unknown (auth-gated)";
      }
    } catch {
      apiVersion = "unknown";
    }

    const stampTag = stampGatewayTag ?? "unknown";
    const gatewayTagMatch: boolean | "unknown" =
      liveGatewayTag === "unknown" || stampTag === "unknown" ? "unknown" : liveGatewayTag === stampTag;

    return { liveGatewayTag, stampGatewayTag: stampTag, gatewayTagMatch, apiVersion };
  }
}
